// src/pages/Booking.tsx

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';

import {
  User,
  Activity,
  Heart,
  Shirt,
  CheckCircle2,
  ChevronRight,
  ChevronLeft,
  Edit2,
  AlertCircle,
  Loader2,
} from 'lucide-react';

// ============================================
// Types
// ============================================

type Step = 1 | 2 | 3 | 4 | 5;
type InjurySustainability = 'permanent' | 'temporary' | '';

interface FormData {
  fullName: string;
  birthDate: string;
  nationalId: string;
  primaryPhone: string;
  location: string;
  emergencyPhone: string;

  position: string;

  playedBefore: boolean | null;
  previousExperience: string;

  registeredElsewhere: boolean | null;
  clubName: string;
  registeredFederation: string;

  preferredFoot: string;

  hasWarInjuries: boolean | null;
  warInjuryType: string;

  hasSportsInjuries: boolean | null;
  sportsInjuryType: string;
  injurySustainability: InjurySustainability;

  uniformSize: string;
  shoeSize: string;

  dataAccuracyAgreement: boolean;
}

interface FieldError {
  [key: string]: string;
}

// ============================================
// Constants
// ============================================

const STORAGE_KEY = 'champions_booking_draft';
const PENDING_BOOKINGS_KEY = 'champions_pending_bookings';
const LOCAL_API_ENDPOINT = '/api/booking.php';
const REQUEST_TIMEOUT = 15000;

// Registration status - set to true to close registration
const REGISTRATION_CLOSED = true;

// ============================================
// Main Component
// ============================================

const Booking = () => {
  const { i18n } = useTranslation();
  const { toast } = useToast();
  const isRTL = i18n.language === 'ar';

  const [currentStep, setCurrentStep] = useState<Step>(1);
  const [errors, setErrors] = useState<FieldError>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingNumber, setBookingNumber] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    birthDate: '',
    nationalId: '',
    primaryPhone: '',
    location: '',
    emergencyPhone: '',

    position: '',

    playedBefore: null,
    previousExperience: '',

    registeredElsewhere: null,
    clubName: '',
    registeredFederation: '',

    preferredFoot: '',

    hasWarInjuries: null,
    warInjuryType: '',

    hasSportsInjuries: null,
    sportsInjuryType: '',
    injurySustainability: '',

    uniformSize: '',
    shoeSize: '',

    dataAccuracyAgreement: false,
  });

  const steps = useMemo(
    () => [
      {
        number: 1 as const,
        icon: User,
        titleAr: 'البيانات الأساسية',
        titleEn: 'Basic Information',
        descAr: 'المعلومات الشخصية الأساسية للاعب',
        descEn: 'Basic personal information of the player',
      },
      {
        number: 2 as const,
        icon: Activity,
        titleAr: 'البيانات الفنية',
        titleEn: 'Technical Data',
        descAr: 'معلومات عن المركز والخبرة الرياضية',
        descEn: 'Information about position and sports experience',
      },
      {
        number: 3 as const,
        icon: Heart,
        titleAr: 'البيانات الصحية',
        titleEn: 'Health Data',
        descAr: 'معلومات صحية مهمة لسلامة اللاعب',
        descEn: 'Important health information for player safety',
      },
      {
        number: 4 as const,
        icon: Shirt,
        titleAr: 'بيانات الزي',
        titleEn: 'Kit Information',
        descAr: 'لتوفير الزي المناسب لك',
        descEn: 'To provide you with the right kit',
      },
      {
        number: 5 as const,
        icon: CheckCircle2,
        titleAr: 'المراجعة والتأكيد',
        titleEn: 'Review & Submit',
        descAr: 'راجع بياناتك قبل الإرسال',
        descEn: 'Review your data before submitting',
      },
    ],
    []
  );

  // ============================================
  // Load/Save Draft
  // ============================================

  useEffect(() => {
    const savedDraft = localStorage.getItem(STORAGE_KEY);
    if (savedDraft) {
      try {
        const parsed = JSON.parse(savedDraft);
        setFormData(parsed);
      } catch (error) {
        console.error('Error loading draft:', error);
      }
    }
  }, []);

  useEffect(() => {
    if (formData.fullName || formData.primaryPhone) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
    }
  }, [formData]);

  // ============================================
  // Background Sync for Pending Bookings
  // ============================================

  useEffect(() => {
    const syncPendingBookings = async () => {
      try {
        const pending = JSON.parse(localStorage.getItem(PENDING_BOOKINGS_KEY) || '[]');
        if (pending.length === 0) return;

        const stillPending: any[] = [];

        for (const booking of pending) {
          if (!booking.synced) {
            try {
              const response = await fetch(LOCAL_API_ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(booking.data),
              });
              if (response.ok) {
                booking.synced = true;
              } else {
                stillPending.push(booking);
              }
            } catch (e) {
              console.log('Background sync failed, will retry later');
              stillPending.push(booking);
            }
          }
        }

        localStorage.setItem(PENDING_BOOKINGS_KEY, JSON.stringify(stillPending));
      } catch (e) {
        console.error('Background sync error:', e);
      }
    };

    syncPendingBookings();

    const handleOnline = () => {
      console.log('Back online, syncing pending bookings...');
      syncPendingBookings();
    };

    window.addEventListener('online', handleOnline);
    const syncInterval = setInterval(syncPendingBookings, 5 * 60 * 1000);

    return () => {
      window.removeEventListener('online', handleOnline);
      clearInterval(syncInterval);
    };
  }, []);

  // ============================================
  // Form Handlers
  // ============================================

  const clearError = (field: keyof FormData) => {
    setErrors((prev) => {
      if (!prev[field as string]) return prev;
      const next = { ...prev };
      delete next[field as string];
      return next;
    });
  };

  const updateFields = (patch: Partial<FormData>) => {
    setFormData((prev) => ({ ...prev, ...patch }));
    Object.keys(patch).forEach((k) => {
      if (errors[k]) {
        setErrors((prev) => {
          const next = { ...prev };
          delete next[k];
          return next;
        });
      }
    });
  };

  const updateField = (field: keyof FormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    clearError(field);
  };

  // ============================================
  // Validation Helpers
  // ============================================

  // Palestinian phone: 10 digits, starts with 05
  const isValidPhone = (phone: string): boolean => {
    const cleaned = phone.replace(/[\s\-\(\)]/g, '');
    return /^05\d{8}$/.test(cleaned);
  };

  // Palestinian National ID: 9 digits
  const isValidNationalId = (id: string): boolean => {
    if (!id) return false;
    const cleaned = id.replace(/[\s\-]/g, '');
    return /^\d{9}$/.test(cleaned);
  };

  // Name validation: at least 3 characters
  const isValidName = (name: string): boolean => {
    return name.trim().length >= 3;
  };

  // Calculate age from birth date
  const calculateAge = (birthDate: string): number => {
    if (!birthDate) return 0;
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const validateStep = (): boolean => {
    const newErrors: FieldError = {};

    if (currentStep === 1) {
      // Full Name
      if (!formData.fullName.trim()) {
        newErrors.fullName = isRTL ? 'مطلوب' : 'Required';
      } else if (!isValidName(formData.fullName)) {
        newErrors.fullName = isRTL ? 'الاسم يجب أن يكون 3 أحرف على الأقل' : 'Name must be at least 3 characters';
      }

      // Birth Date
      if (!formData.birthDate) {
        newErrors.birthDate = isRTL ? 'مطلوب' : 'Required';
      } else {
        const age = calculateAge(formData.birthDate);
        if (age < 5) {
          newErrors.birthDate = isRTL ? 'الحد الأدنى للعمر 5 سنوات' : 'Minimum age is 5 years';
        } else if (age > 18) {
          newErrors.birthDate = isRTL ? 'الحد الأقصى للعمر 18 سنة' : 'Maximum age is 18 years';
        }
      }

      // National ID
      if (!formData.nationalId.trim()) {
        newErrors.nationalId = isRTL ? 'مطلوب' : 'Required';
      } else if (!isValidNationalId(formData.nationalId)) {
        newErrors.nationalId = isRTL ? 'رقم الهوية يجب أن يكون 9 أرقام' : 'National ID must be 9 digits';
      }

      // Primary Phone
      if (!formData.primaryPhone.trim()) {
        newErrors.primaryPhone = isRTL ? 'مطلوب' : 'Required';
      } else if (!isValidPhone(formData.primaryPhone)) {
        newErrors.primaryPhone = isRTL ? 'رقم الهاتف يجب أن يكون 10 أرقام ويبدأ بـ 05' : 'Phone must be 10 digits starting with 05';
      }

      // Location
      if (!formData.location) {
        newErrors.location = isRTL ? 'مطلوب' : 'Required';
      }

      // Emergency Phone
      if (!formData.emergencyPhone.trim()) {
        newErrors.emergencyPhone = isRTL ? 'مطلوب' : 'Required';
      } else if (!isValidPhone(formData.emergencyPhone)) {
        newErrors.emergencyPhone = isRTL ? 'رقم الهاتف يجب أن يكون 10 أرقام ويبدأ بـ 05' : 'Phone must be 10 digits starting with 05';
      }
    }

    if (currentStep === 2) {
      if (!formData.position) newErrors.position = isRTL ? 'مطلوب' : 'Required';

      if (formData.playedBefore === null)
        newErrors.playedBefore = isRTL ? 'مطلوب' : 'Required';
      if (formData.playedBefore === true && !formData.previousExperience.trim())
        newErrors.previousExperience = isRTL ? 'مطلوب' : 'Required';

      if (formData.registeredElsewhere === null)
        newErrors.registeredElsewhere = isRTL ? 'مطلوب' : 'Required';
      if (formData.registeredElsewhere === true && !formData.clubName.trim())
        newErrors.clubName = isRTL ? 'مطلوب' : 'Required';

      if (!formData.preferredFoot) newErrors.preferredFoot = isRTL ? 'مطلوب' : 'Required';
    }

    if (currentStep === 3) {
      if (formData.hasWarInjuries === null)
        newErrors.hasWarInjuries = isRTL ? 'مطلوب' : 'Required';
      if (formData.hasWarInjuries === true && !formData.warInjuryType.trim())
        newErrors.warInjuryType = isRTL ? 'مطلوب' : 'Required';

      if (formData.hasSportsInjuries === null)
        newErrors.hasSportsInjuries = isRTL ? 'مطلوب' : 'Required';
      if (formData.hasSportsInjuries === true && !formData.sportsInjuryType.trim())
        newErrors.sportsInjuryType = isRTL ? 'مطلوب' : 'Required';
      if (formData.hasSportsInjuries === true && !formData.injurySustainability)
        newErrors.injurySustainability = isRTL ? 'مطلوب' : 'Required';
    }

    if (currentStep === 4) {
      if (!formData.uniformSize) newErrors.uniformSize = isRTL ? 'مطلوب' : 'Required';
      if (!formData.shoeSize) newErrors.shoeSize = isRTL ? 'مطلوب' : 'Required';
    }

    if (currentStep === 5) {
      if (!formData.dataAccuracyAgreement)
        newErrors.dataAccuracyAgreement = isRTL
          ? 'يجب الإقرار بصحة البيانات قبل الإرسال'
          : 'You must confirm data accuracy before submitting';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ============================================
  // Navigation
  // ============================================

  const nextStep = () => {
    if (!validateStep()) {
      toast({
        title: isRTL ? 'خطأ' : 'Error',
        description: isRTL
          ? 'الرجاء تعبئة جميع الحقول المطلوبة بشكل صحيح'
          : 'Please fill all required fields correctly',
        variant: 'destructive',
      });
      return;
    }

    if (currentStep < 5) {
      setCurrentStep((prev) => (prev + 1) as Step);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    handleSubmit();
  };

  const prevStep = () => {
    if (currentStep <= 1) return;
    setCurrentStep((prev) => (prev - 1) as Step);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goToStep = (step: Step) => {
    setCurrentStep(step);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ============================================
  // Submit
  // ============================================

  const generateBookingNumber = (): string => {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 1000);
    return `BK-${timestamp}-${random}`;
  };

  const fetchWithTimeout = async (url: string, options: RequestInit, timeout: number = REQUEST_TIMEOUT): Promise<Response> => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  };

  const saveToPendingQueue = (bookingData: any): boolean => {
    try {
      const pending = JSON.parse(localStorage.getItem(PENDING_BOOKINGS_KEY) || '[]');
      pending.push({
        data: bookingData,
        savedAt: new Date().toISOString(),
        synced: false,
      });
      localStorage.setItem(PENDING_BOOKINGS_KEY, JSON.stringify(pending));
      return true;
    } catch (e) {
      console.error('Failed to save to pending queue:', e);
      return false;
    }
  };

  const isOnline = (): boolean => {
    return typeof navigator !== 'undefined' ? navigator.onLine : true;
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setErrors({});

    const bookingNum = generateBookingNumber();
    setBookingNumber(bookingNum);

    const age = calculateAge(formData.birthDate);

    const submitData = {
      bookingNumber: bookingNum,
      fullName: formData.fullName,
      birthDate: formData.birthDate,
      age: age,
      nationalId: formData.nationalId,
      primaryPhone: formData.primaryPhone,
      location: formData.location,
      locationLabel: getLocationLabel(formData.location, 'ar'),
      emergencyPhone: formData.emergencyPhone,
      position: formData.position,
      positionLabel: getPositionLabel(formData.position, 'ar'),
      playedBefore: formData.playedBefore,
      previousExperience: formData.previousExperience,
      registeredElsewhere: formData.registeredElsewhere,
      clubName: formData.clubName,
      registeredFederation: formData.registeredFederation,
      preferredFoot: formData.preferredFoot,
      preferredFootLabel: getFootLabel(formData.preferredFoot, 'ar'),
      hasWarInjuries: formData.hasWarInjuries,
      warInjuryType: formData.warInjuryType,
      hasSportsInjuries: formData.hasSportsInjuries,
      sportsInjuryType: formData.sportsInjuryType,
      injurySustainability: formData.injurySustainability,
      injurySustainabilityLabel: getSustainabilityLabel(formData.injurySustainability, 'ar'),
      uniformSize: formData.uniformSize,
      shoeSize: formData.shoeSize,
      dataAccuracyAgreement: formData.dataAccuracyAgreement,
      createdAt: new Date().toISOString(),
    };

    try {
      // Check if online
      if (!isOnline()) {
        const saved = saveToPendingQueue(submitData);
        if (saved) {
          localStorage.removeItem(STORAGE_KEY);
          setShowSuccess(true);
          return;
        }
      }

      // Try to submit to API
      const response = await fetchWithTimeout(LOCAL_API_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitData),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          localStorage.removeItem(STORAGE_KEY);
          setShowSuccess(true);
        } else {
          throw new Error(result.error || 'Unknown error');
        }
      } else {
        throw new Error(`Server error: ${response.status}`);
      }
    } catch (error: any) {
      console.error('Submission error:', error);

      // Save to pending queue as fallback
      const saved = saveToPendingQueue(submitData);
      if (saved) {
        localStorage.removeItem(STORAGE_KEY);
        setShowSuccess(true);
      } else {
        toast({
          title: isRTL ? 'خطأ' : 'Error',
          description: isRTL
            ? 'حدث خطأ أثناء إرسال الطلب. الرجاء المحاولة مرة أخرى'
            : 'Failed to submit request. Please try again',
          variant: 'destructive',
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      fullName: '',
      birthDate: '',
      nationalId: '',
      primaryPhone: '',
      location: '',
      emergencyPhone: '',
      position: '',
      playedBefore: null,
      previousExperience: '',
      registeredElsewhere: null,
      clubName: '',
      registeredFederation: '',
      preferredFoot: '',
      hasWarInjuries: null,
      warInjuryType: '',
      hasSportsInjuries: null,
      sportsInjuryType: '',
      injurySustainability: '',
      uniformSize: '',
      shoeSize: '',
      dataAccuracyAgreement: false,
    });
    setCurrentStep(1);
    setErrors({});
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  // Success Page
  if (showSuccess) {
    return (
      <div dir={isRTL ? 'rtl' : 'ltr'} className="min-h-screen bg-background">
        <section
          className="relative mt-6 w-full bg-cover bg-center bg-no-repeat min-h-[200px] sm:min-h-[300px] md:min-h-[400px] lg:min-h-[500px]"
          style={{
            backgroundImage: "url('/assets/hero/booking-hero.jpeg')",
          }}
        >
          <div className="absolute inset-0 bg-black/10" />
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background via-background/80 to-transparent" />
        </section>

        <div className="container mx-auto px-4 py-12 max-w-2xl">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="bg-card border border-border rounded-2xl shadow-2xl p-8 md:p-12 text-center"
          >
            {/* Success Icon */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              className="w-24 h-24 mx-auto mb-8 rounded-full bg-green-500/20 flex items-center justify-center"
            >
              <CheckCircle2 className="w-14 h-14 text-green-500" />
            </motion.div>

            {/* Title */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-primary to-green-500 bg-clip-text text-transparent"
            >
              {isRTL ? 'تهانينا!' : 'Congratulations!'}
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-xl md:text-2xl font-semibold text-foreground mb-4"
            >
              {isRTL ? 'بانتظارك يا بطل' : 'We are waiting for you, champion!'}
            </motion.p>

            {/* Message */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="text-muted-foreground mb-8 leading-relaxed"
            >
              {isRTL
                ? 'تم استلام طلبك بنجاح. سيتم التواصل معك قريباً لتأكيد موعد التجربة. نتمنى لك التوفيق!'
                : 'Your request has been received successfully. We will contact you soon to confirm your tryout appointment. Good luck!'}
            </motion.p>

            {/* Action Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
            >
              <Button
                onClick={() => {
                  setShowSuccess(false);
                  resetForm();
                }}
                size="lg"
                className="h-14 px-8 text-base font-medium shadow-lg"
              >
                {isRTL ? 'تسجيل لاعب آخر' : 'Register Another Player'}
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} className="min-h-screen bg-background">

      <section
        className="relative mt-6 w-full bg-cover bg-center bg-no-repeat min-h-[200px] sm:min-h-[300px] md:min-h-[400px] lg:min-h-[500px]"
        style={{
          backgroundImage: "url('/assets/hero/booking-hero.jpeg')",
        }}
      >
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black/10" />
        {/* Bottom gradient fade to background */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background via-background/80 to-transparent" />
      </section>

      <div className="container mx-auto px-4 py-12 max-w-4xl relative">
        {/* Registration Closed Overlay */}
        {REGISTRATION_CLOSED && (
          <div className="absolute inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-background/60 backdrop-blur-sm rounded-2xl" />
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="relative z-10 bg-card border border-border rounded-2xl shadow-2xl p-8 md:p-12 text-center max-w-lg mx-4"
            >
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-primary/20 flex items-center justify-center">
                <AlertCircle className="w-10 h-10 text-primary" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-foreground">
                {isRTL ? 'التسجيل مغلق' : 'Registration Closed'}
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                {isRTL
                  ? 'يرجى متابعتنا على وسائل التواصل الاجتماعي لمعرفة آخر التحديثات.'
                  : 'Please follow us on social media for updates.'}
              </p>
            </motion.div>
          </div>
        )}

        {/* Form content - blurred when registration is closed */}
        <div className={REGISTRATION_CLOSED ? 'blur-sm pointer-events-none select-none' : ''}>
        {/* Progress Stepper */}
        <div className="mb-12">
          <div className="flex items-center justify-between gap-1 sm:gap-2 mb-4 sm:mb-6">
            {steps.map((step, index) => {
              const StepIcon = step.icon;
              const isActive = currentStep === step.number;
              const isCompleted = currentStep > step.number;

              return (
                <div key={step.number} className="flex items-center flex-1 min-w-0">
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center border-2 transition-all duration-300 flex-shrink-0 ${
                        isCompleted
                          ? 'bg-primary border-primary text-primary-foreground shadow-lg'
                          : isActive
                          ? 'bg-primary/10 border-primary text-primary shadow-md scale-110'
                          : 'bg-background border-border text-muted-foreground'
                      }`}
                    >
                      <StepIcon className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
                    </div>
                    <p
                      className={`mt-2 sm:mt-3 text-[10px] sm:text-xs md:text-sm font-medium text-center max-w-[60px] sm:max-w-[75px] md:max-w-[90px] line-clamp-2 transition-colors ${
                        isActive ? 'text-primary font-bold' : 'text-muted-foreground'
                      }`}
                    >
                      {isRTL ? step.titleAr : step.titleEn}
                    </p>
                  </div>

                  {index < steps.length - 1 && (
                    <div className="relative flex-1 mx-1 sm:mx-2 md:mx-3 h-1 min-w-0">
                      <div className="absolute inset-0 bg-border rounded-full" />
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-primary to-primary/80 rounded-full"
                        initial={{ width: 0 }}
                        animate={{ width: currentStep > step.number ? '100%' : '0%' }}
                        transition={{ duration: 0.5, ease: 'easeInOut' }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <p className="text-center text-xs sm:text-sm font-medium text-muted-foreground">
            {isRTL ? `الخطوة ${currentStep} من ${steps.length}` : `Step ${currentStep} of ${steps.length}`}
          </p>
        </div>

        {/* Form Card */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: isRTL ? -20 : 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: isRTL ? 20 : -20 }}
            transition={{ duration: 0.3 }}
            className="bg-card border border-border rounded-xl shadow-xl p-8 md:p-10"
          >
            <div className="mb-10">
              <h2 className="text-3xl md:text-4xl font-bold mb-3 bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
                {isRTL ? steps[currentStep - 1].titleAr : steps[currentStep - 1].titleEn}
              </h2>
              <p className="text-base text-muted-foreground">
                {isRTL ? steps[currentStep - 1].descAr : steps[currentStep - 1].descEn}
              </p>
            </div>

            <div className="space-y-7">
              {currentStep === 1 && (
                <Step1 formData={formData} updateField={updateField} errors={errors} isRTL={isRTL} />
              )}
              {currentStep === 2 && (
                <Step2
                  formData={formData}
                  updateFields={updateFields}
                  updateField={updateField}
                  errors={errors}
                  isRTL={isRTL}
                />
              )}
              {currentStep === 3 && (
                <Step3
                  formData={formData}
                  updateFields={updateFields}
                  updateField={updateField}
                  errors={errors}
                  isRTL={isRTL}
                />
              )}
              {currentStep === 4 && (
                <Step4 formData={formData} updateField={updateField} errors={errors} isRTL={isRTL} />
              )}
              {currentStep === 5 && (
                <Step5
                  formData={formData}
                  goToStep={goToStep}
                  updateField={updateField}
                  errors={errors}
                  isRTL={isRTL}
                />
              )}
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Navigation Buttons */}
        <div className={`mt-8 flex gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
          {currentStep > 1 && (
            <Button
              onClick={prevStep}
              variant="outline"
              size="lg"
              className="flex-1 h-14 text-base font-medium"
              disabled={isSubmitting}
            >
              {isRTL ? (
                <>
                  السابق
                  <ChevronRight className="w-5 h-5 mr-2" />
                </>
              ) : (
                <>
                  <ChevronLeft className="w-5 h-5 mr-2" />
                  Previous
                </>
              )}
            </Button>
          )}

          <Button
            onClick={nextStep}
            size="lg"
            className="flex-1 h-14 text-base font-medium shadow-lg"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                {isRTL ? 'جارٍ الإرسال...' : 'Submitting...'}
              </>
            ) : currentStep === 5 ? (
              <>
                {isRTL ? (
                  <>
                    تأكيد وإرسال
                    <CheckCircle2 className="w-5 h-5 ml-2" />
                  </>
                ) : (
                  <>
                    Confirm & Submit
                    <CheckCircle2 className="w-5 h-5 ml-2" />
                  </>
                )}
              </>
            ) : (
              <>
                {isRTL ? (
                  <>
                    التالي
                    <ChevronLeft className="w-5 h-5 ml-2" />
                  </>
                ) : (
                  <>
                    Next
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </>
            )}
          </Button>
        </div>
        </div>{/* End blur wrapper */}
      </div>
    </div>
  );
};

// ============================================
// Helper Components
// ============================================

const RTLLabel = ({
  htmlFor,
  isRTL,
  required,
  children,
}: {
  htmlFor?: string;
  isRTL: boolean;
  required?: boolean;
  children: React.ReactNode;
}) => (
  <Label
    htmlFor={htmlFor}
    className={`flex items-center gap-1.5 mb-3 text-sm font-semibold ${
      isRTL ? 'flex-row-reverse justify-end' : ''
    }`}
  >
    <span>{children}</span>
    {required && <span className="text-red-500 text-base">*</span>}
  </Label>
);

const RTLErrorMessage = ({ error, isRTL }: { error: string; isRTL: boolean }) => (
  <motion.div
    initial={{ opacity: 0, y: -10 }}
    animate={{ opacity: 1, y: 0 }}
    className={`mt-2 px-3 py-2 rounded-md bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900 flex items-center gap-2 ${
      isRTL ? 'flex-row-reverse' : ''
    }`}
  >
    <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
    <p className="text-sm text-red-600 dark:text-red-400 font-medium">{error}</p>
  </motion.div>
);

const TextArea = ({
  id,
  value,
  onChange,
  placeholder,
  rows = 4,
  hasError,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  rows?: number;
  hasError?: boolean;
}) => (
  <textarea
    id={id}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    rows={rows}
    className={`flex w-full rounded-md border bg-background px-4 py-3 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none ${
      hasError ? 'border-red-500 focus-visible:ring-red-500' : 'border-input'
    }`}
  />
);

// ============================================
// Birth Date Input Component
// ============================================

const BirthDateInput = ({
  birthDate,
  onChange,
  error,
  isRTL,
}: {
  birthDate: string;
  onChange: (date: string) => void;
  error?: string;
  isRTL: boolean;
}) => {
  const [day, setDay] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const [initialized, setInitialized] = useState(false);

  // Initialize from birthDate prop only once
  useEffect(() => {
    if (!initialized && birthDate) {
      const parts = birthDate.split('-');
      if (parts.length === 3) {
        const y = parts[0] !== '0000' ? parts[0] : '';
        const m = parts[1] !== '00' ? parts[1].replace(/^0/, '') : '';
        const d = parts[2] !== '00' ? parts[2].replace(/^0/, '') : '';
        setYear(y);
        setMonth(m);
        setDay(d);
      }
      setInitialized(true);
    }
  }, [birthDate, initialized]);

  // Build and send date string when all fields are filled
  const updateDate = (d: string, m: string, y: string) => {
    if (y.length === 4 && m && d) {
      const paddedMonth = m.padStart(2, '0');
      const paddedDay = d.padStart(2, '0');
      onChange(`${y}-${paddedMonth}-${paddedDay}`);
    } else if (!y && !m && !d) {
      onChange('');
    }
  };

  const handleDayChange = (value: string) => {
    const cleaned = value.replace(/\D/g, '').slice(0, 2);
    setDay(cleaned);
  };

  const handleDayBlur = () => {
    if (day && day.length === 1) {
      const padded = day.padStart(2, '0');
      setDay(padded);
      updateDate(padded, month, year);
    } else {
      updateDate(day, month, year);
    }
  };

  const handleMonthChange = (value: string) => {
    const cleaned = value.replace(/\D/g, '').slice(0, 2);
    setMonth(cleaned);
  };

  const handleMonthBlur = () => {
    if (month && month.length === 1) {
      const padded = month.padStart(2, '0');
      setMonth(padded);
      updateDate(day, padded, year);
    } else {
      updateDate(day, month, year);
    }
  };

  const handleYearChange = (value: string) => {
    const cleaned = value.replace(/\D/g, '').slice(0, 4);
    setYear(cleaned);
  };

  const handleYearBlur = () => {
    updateDate(day, month, year);
  };

  return (
    <div>
      <RTLLabel isRTL={isRTL} required>
        {isRTL ? 'تاريخ الميلاد' : 'Birth Date'}
      </RTLLabel>
      <div className={`grid grid-cols-3 gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div>
          <label className="text-xs text-muted-foreground mb-2 block">
            {isRTL ? 'اليوم' : 'Day'}
          </label>
          <Input
            type="text"
            inputMode="numeric"
            placeholder="DD"
            value={day}
            onChange={(e) => handleDayChange(e.target.value)}
            onBlur={handleDayBlur}
            maxLength={2}
            className={`h-12 text-base text-center ${error ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
          />
        </div>

        <div>
          <label className="text-xs text-muted-foreground mb-2 block">
            {isRTL ? 'الشهر' : 'Month'}
          </label>
          <Input
            type="text"
            inputMode="numeric"
            placeholder="MM"
            value={month}
            onChange={(e) => handleMonthChange(e.target.value)}
            onBlur={handleMonthBlur}
            maxLength={2}
            className={`h-12 text-base text-center ${error ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
          />
        </div>

        <div>
          <label className="text-xs text-muted-foreground mb-2 block">
            {isRTL ? 'السنة' : 'Year'}
          </label>
          <Input
            type="text"
            inputMode="numeric"
            placeholder="YYYY"
            value={year}
            onChange={(e) => handleYearChange(e.target.value)}
            onBlur={handleYearBlur}
            maxLength={4}
            className={`h-12 text-base text-center ${error ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
          />
        </div>
      </div>
      {error && <RTLErrorMessage error={error} isRTL={isRTL} />}
    </div>
  );
};

// ============================================
// Step Components
// ============================================

const Step1 = ({
  formData,
  updateField,
  errors,
  isRTL,
}: {
  formData: FormData;
  updateField: (field: keyof FormData, value: any) => void;
  errors: FieldError;
  isRTL: boolean;
}) => (
  <>
    <div>
      <RTLLabel htmlFor="fullName" isRTL={isRTL} required>
        {isRTL ? 'الاسم الرباعي' : 'Full Name'}
      </RTLLabel>
      <Input
        id="fullName"
        value={formData.fullName}
        onChange={(e) => updateField('fullName', e.target.value)}
        placeholder={isRTL ? 'محمد أحمد علي حسن' : 'Mohammed Ahmed Ali Hassan'}
        className={`h-12 text-base ${errors.fullName ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
      />
      {errors.fullName && <RTLErrorMessage error={errors.fullName} isRTL={isRTL} />}
    </div>

    <BirthDateInput
      birthDate={formData.birthDate}
      onChange={(date) => updateField('birthDate', date)}
      error={errors.birthDate}
      isRTL={isRTL}
    />

    <div>
      <RTLLabel htmlFor="nationalId" isRTL={isRTL} required>
        {isRTL ? 'رقم هوية اللاعب (9 أرقام)' : 'National ID (9 digits)'}
      </RTLLabel>
      <Input
        id="nationalId"
        dir="ltr"
        inputMode="numeric"
        value={formData.nationalId}
        onChange={(e) => {
          const value = e.target.value.replace(/\D/g, '').slice(0, 9);
          updateField('nationalId', value);
        }}
        placeholder="123456789"
        maxLength={9}
        className={`h-12 text-base ${errors.nationalId ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
      />
      {errors.nationalId && <RTLErrorMessage error={errors.nationalId} isRTL={isRTL} />}
    </div>

    <div>
      <RTLLabel htmlFor="primaryPhone" isRTL={isRTL} required>
        {isRTL ? 'رقم موبايل للتواصل (10 أرقام)' : 'Primary Contact Phone (10 digits)'}
      </RTLLabel>
      <Input
        id="primaryPhone"
        type="tel"
        dir="ltr"
        inputMode="tel"
        value={formData.primaryPhone}
        onChange={(e) => {
          const value = e.target.value.replace(/\D/g, '').slice(0, 10);
          updateField('primaryPhone', value);
        }}
        placeholder="05XXXXXXXX"
        maxLength={10}
        className={`h-12 text-base ${errors.primaryPhone ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
      />
      {errors.primaryPhone && <RTLErrorMessage error={errors.primaryPhone} isRTL={isRTL} />}
    </div>

    <div>
      <RTLLabel htmlFor="location" isRTL={isRTL} required>
        {isRTL ? 'مكان السكن / النزوح' : 'Residence / Displacement Location'}
      </RTLLabel>
      <Select value={formData.location} onValueChange={(value) => updateField('location', value)}>
        <SelectTrigger className={`h-12 text-base ${errors.location ? 'border-red-500 focus:ring-red-500' : ''}`}>
          <SelectValue placeholder={isRTL ? 'اختر المنطقة' : 'Select Area'} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="gaza">{isRTL ? 'غزة' : 'Gaza'}</SelectItem>
          <SelectItem value="nuseirat">{isRTL ? 'النصيرات' : 'Nuseirat'}</SelectItem>
          <SelectItem value="deir-al-balah">{isRTL ? 'دير البلح' : 'Deir al-Balah'}</SelectItem>
          <SelectItem value="khan-younis">{isRTL ? 'خانيونس' : 'Khan Younis'}</SelectItem>
        </SelectContent>
      </Select>
      {errors.location && <RTLErrorMessage error={errors.location} isRTL={isRTL} />}
    </div>

    <div>
      <RTLLabel htmlFor="emergencyPhone" isRTL={isRTL} required>
        {isRTL ? 'رقم موبايل الطوارئ (10 أرقام)' : 'Emergency Contact Phone (10 digits)'}
      </RTLLabel>
      <Input
        id="emergencyPhone"
        type="tel"
        dir="ltr"
        inputMode="tel"
        value={formData.emergencyPhone}
        onChange={(e) => {
          const value = e.target.value.replace(/\D/g, '').slice(0, 10);
          updateField('emergencyPhone', value);
        }}
        placeholder="05XXXXXXXX"
        maxLength={10}
        className={`h-12 text-base ${errors.emergencyPhone ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
      />
      {errors.emergencyPhone && <RTLErrorMessage error={errors.emergencyPhone} isRTL={isRTL} />}
    </div>
  </>
);

const Step2 = ({
  formData,
  updateField,
  updateFields,
  errors,
  isRTL,
}: {
  formData: FormData;
  updateField: (field: keyof FormData, value: any) => void;
  updateFields: (patch: Partial<FormData>) => void;
  errors: FieldError;
  isRTL: boolean;
}) => (
  <>
    <div>
      <RTLLabel htmlFor="position" isRTL={isRTL} required>
        {isRTL ? 'مركز اللاعب' : 'Player Position'}
      </RTLLabel>
      <Select value={formData.position} onValueChange={(value) => updateField('position', value)}>
        <SelectTrigger className={`h-12 text-base ${errors.position ? 'border-red-500 focus:ring-red-500' : ''}`}>
          <SelectValue placeholder={isRTL ? 'اختر المركز' : 'Select Position'} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="goalkeeper">{isRTL ? 'حارس مرمى' : 'Goalkeeper'}</SelectItem>
          <SelectItem value="defender">{isRTL ? 'مدافع' : 'Defender'}</SelectItem>
          <SelectItem value="midfielder">{isRTL ? 'وسط' : 'Midfielder'}</SelectItem>
          <SelectItem value="forward">{isRTL ? 'مهاجم' : 'Forward'}</SelectItem>
        </SelectContent>
      </Select>
      {errors.position && <RTLErrorMessage error={errors.position} isRTL={isRTL} />}
    </div>

    <div>
      <RTLLabel isRTL={isRTL} required>
        {isRTL ? 'هل اللاعب لعب سابقًا؟' : 'Has the player played before?'}
      </RTLLabel>
      <div className={`grid grid-cols-2 gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <Button
          type="button"
          variant={formData.playedBefore === true ? 'default' : 'outline'}
          onClick={() => updateField('playedBefore', true)}
          className="h-14 text-base font-medium"
        >
          {isRTL ? 'نعم' : 'Yes'}
        </Button>
        <Button
          type="button"
          variant={formData.playedBefore === false ? 'default' : 'outline'}
          onClick={() => updateFields({ playedBefore: false, previousExperience: '' })}
          className="h-14 text-base font-medium"
        >
          {isRTL ? 'لا' : 'No'}
        </Button>
      </div>
      {errors.playedBefore && <RTLErrorMessage error={errors.playedBefore} isRTL={isRTL} />}
    </div>

    <AnimatePresence>
      {formData.playedBefore && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
        >
          <RTLLabel htmlFor="previousExperience" isRTL={isRTL} required>
            {isRTL ? 'الخبرات السابقة' : 'Previous Experience'}
          </RTLLabel>
          <TextArea
            id="previousExperience"
            value={formData.previousExperience}
            onChange={(v) => updateField('previousExperience', v)}
            placeholder={
              isRTL ? 'اذكر الأندية أو الأكاديميات التي لعبت بها' : 'Mention clubs or academies you played for'
            }
            rows={4}
            hasError={!!errors.previousExperience}
          />
          {errors.previousExperience && <RTLErrorMessage error={errors.previousExperience} isRTL={isRTL} />}
        </motion.div>
      )}
    </AnimatePresence>

    <div>
      <RTLLabel isRTL={isRTL} required>
        {isRTL ? 'هل مسجل باسم نادي أو أكاديمية في اتحاد كرة القدم' : 'Is it registered under the name of a club or academy with the Football Association?'}
      </RTLLabel>
      <div className={`grid grid-cols-2 gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <Button
          type="button"
          variant={formData.registeredElsewhere === true ? 'default' : 'outline'}
          onClick={() => updateField('registeredElsewhere', true)}
          className="h-14 text-base font-medium"
        >
          {isRTL ? 'نعم' : 'Yes'}
        </Button>
        <Button
          type="button"
          variant={formData.registeredElsewhere === false ? 'default' : 'outline'}
          onClick={() =>
            updateFields({
              registeredElsewhere: false,
              clubName: '',
              registeredFederation: '',
            })
          }
          className="h-14 text-base font-medium"
        >
          {isRTL ? 'لا' : 'No'}
        </Button>
      </div>
      {errors.registeredElsewhere && <RTLErrorMessage error={errors.registeredElsewhere} isRTL={isRTL} />}
    </div>

    <AnimatePresence>
      {formData.registeredElsewhere && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
          className="space-y-6"
        >
          <div>
            <RTLLabel htmlFor="clubName" isRTL={isRTL} required>
              {isRTL ? 'اكتب اسم النادي أو الأكاديمية المسجل بإسمها' : 'Enter the club or academy name you are registered with'}
            </RTLLabel>
            <Input
              id="clubName"
              value={formData.clubName}
              onChange={(e) => updateField('clubName', e.target.value)}
              placeholder={isRTL ? 'اسم نادي أو أكاديمية في اتحاد كرة القدم' : 'name of a club or academy with the Football Association?'}
              className={`h-12 text-base ${errors.clubName ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
            />
            {errors.clubName && <RTLErrorMessage error={errors.clubName} isRTL={isRTL} />}
          </div>
        </motion.div>
      )}
    </AnimatePresence>

    <div>
      <RTLLabel htmlFor="preferredFoot" isRTL={isRTL} required>
        {isRTL ? 'القدم المفضلة' : 'Preferred Foot'}
      </RTLLabel>
      <Select value={formData.preferredFoot} onValueChange={(value) => updateField('preferredFoot', value)}>
        <SelectTrigger className={`h-12 text-base ${errors.preferredFoot ? 'border-red-500 focus:ring-red-500' : ''}`}>
          <SelectValue placeholder={isRTL ? 'اختر القدم المفضلة' : 'Select Preferred Foot'} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="right">{isRTL ? 'يمنى' : 'Right'}</SelectItem>
          <SelectItem value="left">{isRTL ? 'يسرى' : 'Left'}</SelectItem>
          <SelectItem value="both">{isRTL ? 'كلاهما' : 'Both'}</SelectItem>
        </SelectContent>
      </Select>
      {errors.preferredFoot && <RTLErrorMessage error={errors.preferredFoot} isRTL={isRTL} />}
    </div>
  </>
);

const Step3 = ({
  formData,
  updateField,
  updateFields,
  errors,
  isRTL,
}: {
  formData: FormData;
  updateField: (field: keyof FormData, value: any) => void;
  updateFields: (patch: Partial<FormData>) => void;
  errors: FieldError;
  isRTL: boolean;
}) => (
  <>
    <div>
      <RTLLabel isRTL={isRTL} required>
        {isRTL ? 'هل لدى اللاعب إصابات في الحرب؟' : 'Does the player have war injuries?'}
      </RTLLabel>
      <div className={`grid grid-cols-2 gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <Button
          type="button"
          variant={formData.hasWarInjuries === true ? 'default' : 'outline'}
          onClick={() => updateField('hasWarInjuries', true)}
          className="h-14 text-base font-medium"
        >
          {isRTL ? 'نعم' : 'Yes'}
        </Button>
        <Button
          type="button"
          variant={formData.hasWarInjuries === false ? 'default' : 'outline'}
          onClick={() => updateFields({ hasWarInjuries: false, warInjuryType: '' })}
          className="h-14 text-base font-medium"
        >
          {isRTL ? 'لا' : 'No'}
        </Button>
      </div>
      {errors.hasWarInjuries && <RTLErrorMessage error={errors.hasWarInjuries} isRTL={isRTL} />}
    </div>

    <AnimatePresence>
      {formData.hasWarInjuries && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
        >
          <RTLLabel htmlFor="warInjuryType" isRTL={isRTL} required>
            {isRTL ? 'نوع الإصابة' : 'Injury Type'}
          </RTLLabel>
          <TextArea
            id="warInjuryType"
            value={formData.warInjuryType}
            onChange={(v) => updateField('warInjuryType', v)}
            placeholder={isRTL ? 'اذكر نوع الإصابة' : 'Describe the injury type'}
            rows={4}
            hasError={!!errors.warInjuryType}
          />
          {errors.warInjuryType && <RTLErrorMessage error={errors.warInjuryType} isRTL={isRTL} />}
        </motion.div>
      )}
    </AnimatePresence>

    <div>
      <RTLLabel isRTL={isRTL} required>
        {isRTL ? 'هل لدى اللاعب إصابات رياضية؟' : 'Does the player have sports injuries?'}
      </RTLLabel>
      <div className={`grid grid-cols-2 gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <Button
          type="button"
          variant={formData.hasSportsInjuries === true ? 'default' : 'outline'}
          onClick={() => updateField('hasSportsInjuries', true)}
          className="h-14 text-base font-medium"
        >
          {isRTL ? 'نعم' : 'Yes'}
        </Button>
        <Button
          type="button"
          variant={formData.hasSportsInjuries === false ? 'default' : 'outline'}
          onClick={() =>
            updateFields({
              hasSportsInjuries: false,
              sportsInjuryType: '',
              injurySustainability: '',
            })
          }
          className="h-14 text-base font-medium"
        >
          {isRTL ? 'لا' : 'No'}
        </Button>
      </div>
      {errors.hasSportsInjuries && <RTLErrorMessage error={errors.hasSportsInjuries} isRTL={isRTL} />}
    </div>

    <AnimatePresence>
      {formData.hasSportsInjuries && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
          className="space-y-6"
        >
          <div>
            <RTLLabel htmlFor="sportsInjuryType" isRTL={isRTL} required>
              {isRTL ? 'نوع الإصابة الرياضية' : 'Sports Injury Type'}
            </RTLLabel>
            <TextArea
              id="sportsInjuryType"
              value={formData.sportsInjuryType}
              onChange={(v) => updateField('sportsInjuryType', v)}
              placeholder={isRTL ? 'مثال: شد عضلي بالركبة...' : 'Example: Knee strain...'}
              rows={4}
              hasError={!!errors.sportsInjuryType}
            />
            {errors.sportsInjuryType && <RTLErrorMessage error={errors.sportsInjuryType} isRTL={isRTL} />}
          </div>

          <div>
            <RTLLabel htmlFor="injurySustainability" isRTL={isRTL} required>
              {isRTL ? 'هل الإصابة مستدامة؟' : 'Is the injury permanent?'}
            </RTLLabel>
            <Select
              value={formData.injurySustainability}
              onValueChange={(value) => updateField('injurySustainability', value as InjurySustainability)}
            >
              <SelectTrigger
                className={`h-12 text-base ${errors.injurySustainability ? 'border-red-500 focus:ring-red-500' : ''}`}
              >
                <SelectValue placeholder={isRTL ? 'اختر' : 'Select'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="permanent">{isRTL ? 'مستدامة' : 'Permanent'}</SelectItem>
                <SelectItem value="temporary">{isRTL ? 'غير مستدامة / مؤقتة' : 'Temporary'}</SelectItem>
              </SelectContent>
            </Select>
            {errors.injurySustainability && <RTLErrorMessage error={errors.injurySustainability} isRTL={isRTL} />}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </>
);

const Step4 = ({
  formData,
  updateField,
  errors,
  isRTL,
}: {
  formData: FormData;
  updateField: (field: keyof FormData, value: any) => void;
  errors: FieldError;
  isRTL: boolean;
}) => (
  <>
    <div>
      <RTLLabel htmlFor="uniformSize" isRTL={isRTL} required>
        {isRTL ? 'مقاس الزي الرياضي' : 'Uniform Size'}
      </RTLLabel>

      <Select value={formData.uniformSize} onValueChange={(value) => updateField('uniformSize', value)}>
        <SelectTrigger
          className={`h-12 text-base ${errors.uniformSize ? 'border-red-500 focus:ring-red-500' : ''}`}
        >
          <SelectValue placeholder={isRTL ? 'اختر المقاس' : 'Select Size'} />
        </SelectTrigger>

        <SelectContent>
          <SelectItem value="10">10</SelectItem>
          <SelectItem value="12">12</SelectItem>
          <SelectItem value="14">14</SelectItem>
          <SelectItem value="16">16</SelectItem>
          <SelectItem value="18">18</SelectItem>
          <SelectItem value="xs">XS</SelectItem>
          <SelectItem value="s">S</SelectItem>
          <SelectItem value="m">M</SelectItem>
          <SelectItem value="l">L</SelectItem>
          <SelectItem value="xl">XL</SelectItem>
        </SelectContent>
      </Select>

      {errors.uniformSize && <RTLErrorMessage error={errors.uniformSize} isRTL={isRTL} />}
    </div>

    <div>
      <RTLLabel htmlFor="shoeSize" isRTL={isRTL} required>
        {isRTL ? 'مقاس الحذاء الرياضي' : 'Shoe Size'}
      </RTLLabel>

      <Select value={formData.shoeSize} onValueChange={(value) => updateField('shoeSize', value)}>
        <SelectTrigger
          className={`h-12 text-base ${errors.shoeSize ? 'border-red-500 focus:ring-red-500' : ''}`}
        >
          <SelectValue placeholder={isRTL ? 'اختر المقاس' : 'Select Size'} />
        </SelectTrigger>

        <SelectContent>
          {Array.from({ length: 17 }, (_, i) => i + 30).map((size) => (
            <SelectItem key={size} value={size.toString()}>
              {size}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {errors.shoeSize && <RTLErrorMessage error={errors.shoeSize} isRTL={isRTL} />}
    </div>
  </>
);

const Step5 = ({
  formData,
  goToStep,
  updateField,
  errors,
  isRTL,
}: {
  formData: FormData;
  goToStep: (step: Step) => void;
  updateField: (field: keyof FormData, value: any) => void;
  errors: FieldError;
  isRTL: boolean;
}) => {
  const ReviewSection = ({
    title,
    icon: Icon,
    step,
    children,
  }: {
    title: string;
    icon: any;
    step: Step;
    children: React.ReactNode;
  }) => (
    <div className="border-2 border-border rounded-xl p-6 bg-muted/30 hover:border-primary/50 transition-colors">
      <div className={`flex items-center justify-between mb-5 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon className="w-5 h-5 text-primary" />
          </div>
          <h3 className="text-lg font-bold">{title}</h3>
        </div>

        <Button
          variant="ghost"
          size="sm"
          onClick={() => goToStep(step)}
          className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}
        >
          <Edit2 className="w-4 h-4" />
          {isRTL ? 'تعديل' : 'Edit'}
        </Button>
      </div>

      <div className="space-y-3">{children}</div>
    </div>
  );

  const ReviewItem = ({ label, value }: { label: string; value: string }) => (
    <div className="flex items-start gap-3 text-sm">
      <span
        className={`text-muted-foreground font-medium min-w-[170px] ${
          isRTL ? 'text-right' : 'text-left'
        }`}
      >
        {label}:
      </span>
      <span className={`font-semibold flex-1 ${isRTL ? 'text-right' : 'text-left'}`}>
        {value || '-'}
      </span>
    </div>
  );

  return (
    <div className="space-y-6">
      <ReviewSection title={isRTL ? 'البيانات الأساسية' : 'Basic Information'} icon={User} step={1}>
        <ReviewItem label={isRTL ? 'الاسم الرباعي' : 'Full Name'} value={formData.fullName} />
        <ReviewItem label={isRTL ? 'تاريخ الميلاد' : 'Birth Date'} value={formData.birthDate} />
        <ReviewItem label={isRTL ? 'رقم الهوية' : 'National ID'} value={formData.nationalId} />
        <ReviewItem label={isRTL ? 'رقم الموبايل الأساسي' : 'Primary Phone'} value={formData.primaryPhone} />
        <ReviewItem
          label={isRTL ? 'مكان السكن/النزوح' : 'Location'}
          value={getLocationLabel(formData.location, isRTL)}
        />
        <ReviewItem label={isRTL ? 'رقم موبايل الطوارئ' : 'Emergency Phone'} value={formData.emergencyPhone} />
      </ReviewSection>

      <ReviewSection title={isRTL ? 'البيانات الفنية' : 'Technical Data'} icon={Activity} step={2}>
        <ReviewItem label={isRTL ? 'المركز' : 'Position'} value={getPositionLabel(formData.position, isRTL)} />

        <ReviewItem
          label={isRTL ? 'لعب سابقًا' : 'Played Before'}
          value={
            formData.playedBefore === null
              ? '-'
              : formData.playedBefore
              ? isRTL
                ? 'نعم'
                : 'Yes'
              : isRTL
              ? 'لا'
              : 'No'
          }
        />

        {formData.playedBefore && (
          <ReviewItem label={isRTL ? 'الخبرات السابقة' : 'Previous Experience'} value={formData.previousExperience} />
        )}

        <ReviewItem
          label={isRTL ? 'مسجل لدى نادٍ آخر' : 'Registered Elsewhere'}
          value={
            formData.registeredElsewhere === null
              ? '-'
              : formData.registeredElsewhere
              ? isRTL
                ? 'نعم'
                : 'Yes'
              : isRTL
              ? 'لا'
              : 'No'
          }
        />

        {formData.registeredElsewhere && (
          <ReviewItem label={isRTL ? 'اسم النادي/الأكاديمية' : 'Club/Academy Name'} value={formData.clubName} />
        )}

        <ReviewItem label={isRTL ? 'القدم المفضلة' : 'Preferred Foot'} value={getFootLabel(formData.preferredFoot, isRTL)} />
      </ReviewSection>

      <ReviewSection title={isRTL ? 'البيانات الصحية' : 'Health Data'} icon={Heart} step={3}>
        <ReviewItem
          label={isRTL ? 'إصابات الحرب' : 'War Injuries'}
          value={
            formData.hasWarInjuries === null
              ? '-'
              : formData.hasWarInjuries
              ? isRTL
                ? 'نعم'
                : 'Yes'
              : isRTL
              ? 'لا'
              : 'No'
          }
        />

        {formData.hasWarInjuries && (
          <ReviewItem label={isRTL ? 'نوع الإصابة' : 'Injury Type'} value={formData.warInjuryType} />
        )}

        <ReviewItem
          label={isRTL ? 'إصابات رياضية' : 'Sports Injuries'}
          value={
            formData.hasSportsInjuries === null
              ? '-'
              : formData.hasSportsInjuries
              ? isRTL
                ? 'نعم'
                : 'Yes'
              : isRTL
              ? 'لا'
              : 'No'
          }
        />

        {formData.hasSportsInjuries && (
          <>
            <ReviewItem
              label={isRTL ? 'نوع الإصابة الرياضية' : 'Sports Injury Type'}
              value={formData.sportsInjuryType}
            />
            <ReviewItem
              label={isRTL ? 'هل الإصابة مستدامة؟' : 'Is the injury permanent?'}
              value={getSustainabilityLabel(formData.injurySustainability, isRTL)}
            />
          </>
        )}
      </ReviewSection>

      <ReviewSection title={isRTL ? 'بيانات الزي' : 'Kit Information'} icon={Shirt} step={4}>
        <ReviewItem label={isRTL ? 'مقاس الزي' : 'Uniform Size'} value={(formData.uniformSize || '-').toString().toUpperCase()} />
        <ReviewItem label={isRTL ? 'مقاس الحذاء' : 'Shoe Size'} value={formData.shoeSize} />
      </ReviewSection>

      <div className="border border-border rounded-xl p-5 bg-muted/20">
        <label
          className={`flex gap-4 p-4 rounded-xl border transition
            cursor-pointer
            ${isRTL ? 'flex-row-reverse text-right' : 'text-left'}
            ${
              formData.dataAccuracyAgreement
                ? 'border-primary bg-primary/5'
                : 'border-gray-200 hover:border-primary/50'
            }
          `}
        >
          <input
            type="checkbox"
            checked={formData.dataAccuracyAgreement}
            onChange={(e) =>
              updateField('dataAccuracyAgreement', e.target.checked)
            }
            className="mt-1 h-5 w-5 shrink-0 accent-primary"
          />

          <span className="text-sm leading-7 text-gray-300">
            {isRTL
              ? 'أُقرّ أنا اللاعب بأن جميع البيانات المُدخلة صحيحة، وأنني خالٍ من أي أمراض أو إصابات قد تعيق مشاركتي. كما أتعهد بالالتزام بتعليمات أكاديمية "تشامبيونز"، وأتحمل المسؤولية الكاملة عن صحة هذه المعلومات وعن سلامتي أثناء المشاركة.'
              : 'I declare that all submitted information is accurate and complete, and that I am free from any illness or injury that may hinder my participation. I also agree to comply with the rules of Champions Academy and take full responsibility for the accuracy of this information and my safety during participation.'}
          </span>
        </label>

        {errors.dataAccuracyAgreement && (
          <div className="mt-3">
            <RTLErrorMessage error={errors.dataAccuracyAgreement} isRTL={isRTL} />
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// Label helpers
// ============================================

function getLocationLabel(value: string, isRTL: boolean | string) {
  const lang = typeof isRTL === 'boolean' ? (isRTL ? 'ar' : 'en') : isRTL;
  const locations: any = {
    gaza: { ar: 'غزة', en: 'Gaza' },
    nuseirat: { ar: 'النصيرات', en: 'Nuseirat' },
    'deir-al-balah': { ar: 'دير البلح', en: 'Deir al-Balah' },
    'khan-younis': { ar: 'خانيونس', en: 'Khan Younis' },
  };
  return locations[value] ? locations[value][lang] : value;
}

function getPositionLabel(value: string, isRTL: boolean | string) {
  const lang = typeof isRTL === 'boolean' ? (isRTL ? 'ar' : 'en') : isRTL;
  const positions: any = {
    goalkeeper: { ar: 'حارس مرمى', en: 'Goalkeeper' },
    defender: { ar: 'مدافع', en: 'Defender' },
    midfielder: { ar: 'وسط', en: 'Midfielder' },
    forward: { ar: 'مهاجم', en: 'Forward' },
  };
  return positions[value] ? positions[value][lang] : value;
}

function getFootLabel(value: string, isRTL: boolean | string) {
  const lang = typeof isRTL === 'boolean' ? (isRTL ? 'ar' : 'en') : isRTL;
  const feet: any = {
    right: { ar: 'يمنى', en: 'Right' },
    left: { ar: 'يسرى', en: 'Left' },
    both: { ar: 'كلاهما', en: 'Both' },
  };
  return feet[value] ? feet[value][lang] : value;
}

function getSustainabilityLabel(value: InjurySustainability, isRTL: boolean | string) {
  const lang = typeof isRTL === 'boolean' ? (isRTL ? 'ar' : 'en') : isRTL;
  const map: any = {
    permanent: { ar: 'مستدامة', en: 'Permanent' },
    temporary: { ar: 'غير مستدامة / مؤقتة', en: 'Temporary' },
  };
  return map[value] ? map[value][lang] : value || '-';
}

export default Booking;
